<template>
	<section class="alert-layout-content" :class="{ 'prevent-events': noGMB }">
		<span v-if="PC_ALERT_NOTES[`${alert.name}`]" class="b360-form-label">
			{{ PC_ALERT_NOTES[alert.name] }}
		</span>
		<AlertRateWatchLayout
			v-if="isRateWatch"
			:rateWatch="alert"
			:alertIndex="alertIndex"
		/>
		<section v-if="alert.name === 'EquityWatch'" class="b360-grid-row">
			<FormInputs
				:name="firstSection.name"
				:label="firstSection.label"
				:type="firstSection.type"
				:value="alert.parameters.LTV"
				:formField="firstSection.formField"
				:placeholder="firstSection.placeholder"
				:tooltip="firstSection.tooltip"
				:max="firstSection.max"
				:min="firstSection.min"
				:step="firstSection.step"
				:pattern="firstSection.pattern"
				@handle-input="
					(e) =>
						this.updateAlertValue({
							preferenceIndex: alertIndex,
							key: 'parameters.LTV',
							value: e.value,
						})
				"
				class="b360-col-12 ltv-field"
				:disabled="disabled"
			/>
		</section>

		<FormRadioButtons
			:legendLabel="`${PC_ALERT_NAME_LEGEND_LABEL[`${alert.name}`]}`"
			:radioGroup="secondSection"
			@handle-input="updateOptedIn"
			:disabled="isDisabled"
		/>
		<FormCheckboxButtons
			v-show="secondSection[0].checked"
			legendLabel="Your Notifications"
			:checkboxGroup="thirdSection"
			:disabled="isDisabled"
			@handle-input="(e) => this.handleBrokerNotificationsUpdate(e)"
		/>
		<FormRadioButtons
			v-show="secondSection[0].checked"
			:id="`borrower-notification-mode-${alertClassName}`"
			legendLabel="Borrowers Notification Mode"
			:radioGroup="fourthSection"
			@handle-input="updateBorrowerNotification"
			:disabled="isDisabled"
		/>
		<FormCheckboxButtons
			legendLabel="Borrower Notifications"
			v-if="fourthSection[0].checked && secondSection[0].checked"
			:checkboxGroup="fifthSection"
			:disabled="true"
		/>
		<div
			class="b360-col-12"
			v-if="fourthSection[0].checked && secondSection[0].checked"
		>
			<span class="b360-form-label" role="heading">
				{{ ALERT_EMAIL_TEMPLATES['label'] }}
			</span>
		</div>
		<section
			class="b360-section-container b360-grid-row"
			v-if="fourthSection[0].checked && secondSection[0].checked"
		>
			<PreferenceCenterImageRadio
				v-for="(card, index) in ALERT_EMAIL_TEMPLATES[alert.name]"
				:alertName="alert.name"
				:key="index"
				:card="card"
				:isChecked="isChecked(index)"
				:disabled="isDisabled"
				@clicked="handleClicked(index)"
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-6"
			/>
		</section>
	</section>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import PreferenceCenterImageRadio from '@/components/preference-center/PreferenceCenterImageRadio.vue';
import FormRadioButtons from '@/components/global/FormRadioButtons.vue';
import FormCheckboxButtons from '@/components/global/FormCheckboxButtons.vue';
import FormInputs from '@/components/global/FormInputs.vue';
import {
	ALERT_EMAIL_TEMPLATES,
	PC_ALERT_NOTES,
	PC_ALERT_NAME_LEGEND_LABEL,
	setAbbreviation,
	setClassNames,
} from '@/components/preference-center/constants/preference-center-alerts-constants.js';
import AlertRateWatchLayout from './AlertRateWatchLayout.vue';

export default {
	name: 'AlertLayout',
	components: {
		FormCheckboxButtons,
		FormInputs,
		AlertRateWatchLayout,
		FormRadioButtons,
		PreferenceCenterImageRadio,
	},
	props: {
		alert: {},
		alertIndex: Number,
		noGMB: Boolean,
		disabled: Boolean,
		isRateWatch: Boolean,
	},
	data() {
		return {
			ALERT_EMAIL_TEMPLATES,
			PC_ALERT_NAME_LEGEND_LABEL,
			PC_ALERT_NOTES,
			selectedIndex: null,
			alertAbbreviation: '',
			alertClassName: '',
			firstSection: {},
			secondSection: [],
			thirdSection: [],
			fourthSection: [],
			fifthSection: [],
		};
	},
	async created() {
		this.alertAbbreviation = setAbbreviation(this.alert.name);
		this.alertClassName = setClassNames(this.alert.name);
		await this.setAlertSections();

		let emailTemplateInitialIndex = this.alert.emailTemplateId;
		switch (true) {
			case emailTemplateInitialIndex.includes('001'):
				this.selectedIndex = 0;
				break;
			case emailTemplateInitialIndex.includes('002'):
				this.selectedIndex = 1;
				break;
			case emailTemplateInitialIndex.includes('003'):
				this.selectedIndex = 2;
				break;
			case emailTemplateInitialIndex.includes('004'):
				this.selectedIndex = 3;
				break;
			default:
				this.selectedIndex = null;
				break;
		}
	},
	computed: {
		...mapGetters([
			'isPermissionActive',
			'hasAccessToClientConnect',
			'getPreferenceIndex',
		]),
		...mapState({}),
		brokerNotifications() {
			let brokerIndex = this.alert.recipientNotifications.findIndex(
				(x) => x.recipientType === 'Broker',
			);
			return this.alert.recipientNotifications[brokerIndex];
		},
		borrowerNotificationIndex() {
			return this.alert.recipientNotifications.findIndex(
				(x) => x.recipientType === 'Borrower',
			);
		},
		brokerEmailIndex() {
			return this.brokerNotifications.notifications.findIndex(
				(x) => x.notificationType === 'Email',
			);
		},
		brokerTextIndex() {
			return this.brokerNotifications.notifications.findIndex(
				(x) => x.notificationType === 'Text',
			);
		},
		isDisabled() {
			if (this.alert.name === 'ReviewMe') {
				return this.noGMB;
			} else return this.disabled;
		},
	},
	methods: {
		...mapActions([
			'selectImage',
			'updateAlertValue',
			'updateEmailTextValues',
			'updateBorrowerEmailNotification',
		]),
		handleClicked(index) {
			this.selectedIndex = parseInt(index);
			this.updateAlertValue({
				preferenceIndex: this.alertIndex,
				key: 'emailTemplateId',
				value: ALERT_EMAIL_TEMPLATES[this.alert.name][index].value,
			});
		},
		isChecked(index) {
			if (index === this.selectedIndex) {
				return true;
			}
			return false;
		},
		handleBrokerNotificationsUpdate(e) {
			let containsEmail = e.fieldName.includes('Email');
			if (containsEmail) {
				this.updateEmailTextValues({
					preferenceIndex: this.alertIndex,
					index: this.brokerEmailIndex,
					value: e.value,
				});
			} else {
				this.updateEmailTextValues({
					preferenceIndex: this.alertIndex,
					index: this.brokerTextIndex,
					value: e.value,
				});
			}
		},
		updateOptedIn(e) {
			this.updateAlertValue({
				preferenceIndex: this.alertIndex,
				key: 'isOptedIn',
				value: e.value,
			});
			if (e.value === true) {
				//if user opts in to alert, update broker email notification activeInd to true as will borrower notification
				this.updateEmailTextValues({
					preferenceIndex: this.alertIndex,
					index: this.brokerEmailIndex,
					value: true,
				});
				this.thirdSection[0].checked = true;
				this.updateBorrowerEmailNotification({
					preferenceIndex: this.alertIndex,
					borrowerIndex: this.borrowerNotificationIndex,
					value: true,
				});
				this.fifthSection[0].checked = true;
			} else {
				//if user opts out, set broker email, text, and borrower email notifications activeInd to false
				this.updateEmailTextValues({
					preferenceIndex: this.alertIndex,
					index: this.brokerEmailIndex,
					value: false,
				});
				this.thirdSection[0].checked = false;
				this.updateEmailTextValues({
					preferenceIndex: this.alertIndex,
					index: this.brokerTextIndex,
					value: false,
				});
				this.thirdSection[1].checked = false;

				this.updateBorrowerEmailNotification({
					preferenceIndex: this.alertIndex,
					borrowerIndex: this.borrowerNotificationIndex,
					value: false,
				});
				this.fifthSection[0].checked = false;
			}
		},
		updateBorrowerNotification(e) {
			//if the autoschedule is true, turn on borrower notification email activeInd to true, if manual, set to false
			this.updateAlertValue({
				preferenceIndex: this.alertIndex,
				key: 'isAutoScheduled',
				value: e.value,
			});
			if (e.value === true) {
				this.updateBorrowerEmailNotification({
					preferenceIndex: this.alertIndex,
					borrowerIndex: this.borrowerNotificationIndex,
					value: true,
				});
				this.fifthSection[0].checked = true;
			} else {
				this.updateBorrowerEmailNotification({
					preferenceIndex: this.alertIndex,
					borrowerIndex: this.borrowerNotificationIndex,
					value: false,
				});
				this.fifthSection[0].checked = false;
			}
		},
		async setAlertSections() {
			if (this.alert.name === 'EquityWatch') {
				this.firstSection = {
					name: `${this.alertClassName}-target-ltv`,
					label: 'Target LTV',
					type: 'number',
					placeholder: '',
					pattern: '\\d{0,3}[\.]\\d{0,2}',
					required: false,
					formField: '80',
					value: null,
					tooltip: 'Set your target LTV number here.',
					disabled: false,
					section: this.alertAbbreviation,
					max: 150,
					min: 0,
					step: 0.01,
				};
			} else {
				this.firstSection = null;
			}
			this.secondSection = [
				{
					name: `${this.alertClassName}`,
					label: 'Enabled: Show alerts in Client Connect.',
					value: true,
					id: 0,
					section: `${this.alertAbbreviation}`,
					checked: this.alert.isOptedIn,
				},
				{
					name: `${this.alertClassName}`,
					label: 'Disabled: Do not show alerts in Client Connect.',
					value: false,
					id: 1,
					section: `${this.alertAbbreviation}`,
					checked: !this.alert.isOptedIn,
				},
			];
			this.thirdSection = [
				{
					name: `${this.alertClassName}-your-notifications`,
					label: 'Email: Please email me real-time summaries about this alert.',
					value: 'email_message',
					id: 1,
					section: `${this.alertAbbreviation}`,
					checked:
						this.brokerNotifications.notifications[this.brokerEmailIndex]
							.activeInd,
				},
				{
					name: `${this.alertClassName}-your-notifications`,
					label:
						'Text Message: Please text me for each notification on this alert.',
					value: 'text_message',
					id: 0,
					section: `${this.alertAbbreviation}`,
					checked:
						this.brokerNotifications.notifications[this.brokerTextIndex]
							.activeInd,
				},
			];
			this.fourthSection = [
				{
					name: `${this.alertClassName}-borrowers-notification-mode`,
					label:
						'Automatic: We will automatically reach out to your borrowers on your behalf.',
					value: true,
					id: 0,
					section: `${this.alertAbbreviation}`,
					checked: this.alert.isAutoScheduled,
				},
				{
					name: `${this.alertClassName}-borrowers-notification-mode`,
					label:
						'Manual: You have the ability to contact the borrower at your own convenience.',
					value: false,
					id: 1,
					section: `${this.alertAbbreviation}`,
					checked: !this.alert.isAutoScheduled,
				},
			];
			this.fifthSection = [
				{
					name: `${this.alertClassName}-borrowers-notifications`,
					label:
						'Email: We will automatically email your borrower and copy you.',
					value: 'email_message',
					id: 0,
					section: `${this.alertAbbreviation}`,
					checked: this.alert.isAutoScheduled,
				},
			];
		},
	},
};
</script>

<style lang="scss" scoped></style>
